import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/Form";
import { Spinner } from "react-bootstrap";
import ApiConfig from "../../api/ApiConfig";
import {
  getWithAuthCall,
  simplePUTCall,
  simplePostCall,
  postMultipartWithAuthCallWithErrorResponse,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { inRange, isNonNegativeNumber, isPositiveNumber } from "./ffHelpers";
import { useFfAccess } from "./ffAccess";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

// FF-18: a cleared field is sent as null, never coerced to 0
// INT-13: modules that can auto-post to the ledger
const AUTO_POST_MODULES = [
  { key: "accident", label: "Vehicle Accident" },
  { key: "accessory", label: "Vehicle Accessory" },
  { key: "fuel", label: "Fuel Expense" },
  { key: "fine", label: "Vehicle Fine" },
  { key: "spare_parts", label: "Spare Parts" },
  { key: "maintenance", label: "Maintenance" },
  { key: "vehicle", label: "Vehicle Acquisition" },
];

const numOrNull = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);
const isBlank = (v) => v === "" || v === null || v === undefined;

const TYPE_PAGE_SIZE = 25; // U-10

const FinanceSettings = () => {
  const { sidebar } = useContext(AppContext);
  const { canManage } = useFfAccess();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState({
    currency_code: "",
    corporate_tax_rate: "",
    tax_depreciation_rate: "",
    default_expected_km: "",
    default_planned_cpk: "",
  });
  const [currencyName, setCurrencyName] = useState("");
  // INT-13
  const [autoPost, setAutoPost] = useState({});
  const [autoPostShape, setAutoPostShape] = useState(null);

  // Per-vehicle-type planned CPK
  const [typeCpk, setTypeCpk] = useState([]);
  const [typeCpkLoading, setTypeCpkLoading] = useState(true);
  const [savingTypeId, setSavingTypeId] = useState(null);
  // U-10: loaded values (to detect changes), search, paging, bulk save
  const [loadedCpk, setLoadedCpk] = useState({});
  const [typeSearch, setTypeSearch] = useState("");
  const [typePage, setTypePage] = useState(1);
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    getProfileCurrency();
    getSettings();
    getTypeCpk();
  }, []);

  // Currency is driven by the Customer Profile (Master Settings), not entered here
  const getProfileCurrency = () => {
    postMultipartWithAuthCallWithErrorResponse(ApiConfig.GET_CUSTOMER_PROFILE)
      .then((res) => {
        const d = res?.json?.data;
        if (res?.json?.result && d) {
          setForm((prev) => ({
            ...prev,
            currency_code: d.currency_code || prev.currency_code,
          }));
          setCurrencyName(
            [d.currency_name, d.currency_symbol && `(${d.currency_symbol})`]
              .filter(Boolean)
              .join(" ")
          );
        }
      })
      .catch((err) => console.log("profile currency err", err));
  };

  const getSettings = () => {
    setLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_SETTINGS)
      .then((data) => {
        const d = data?.data || data?.settings || {};
        if (data?.success || data?.result || d) {
          setForm((prev) => ({
            ...prev,
            // currency_code is sourced from the Customer Profile, not finance settings
            corporate_tax_rate: d.corporate_tax_rate ?? "",
            tax_depreciation_rate: d.tax_depreciation_rate ?? "",
            default_expected_km: d.default_expected_km ?? "",
            default_planned_cpk: d.default_planned_cpk ?? "",
          }));
          // INT-13: per-module auto-posting; accept array of enabled names or {module: bool}
          const ap = d.auto_post_modules;
          if (Array.isArray(ap)) {
            setAutoPostShape("array");
            setAutoPost(Object.fromEntries(AUTO_POST_MODULES.map((m) => [m.key, ap.includes(m.key)])));
          } else if (ap && typeof ap === "object") {
            setAutoPostShape("object");
            setAutoPost(
              Object.fromEntries(
                AUTO_POST_MODULES.map((m) => [m.key, ap[m.key] !== false && ap[m.key] !== 0 && ap[m.key] !== "false"])
              )
            );
          } else {
            setAutoPostShape(null);
          }
        }
      })
      .catch((err) => console.log("settings err", err))
      .finally(() => setLoading(false));
  };

  const getTypeCpk = () => {
    setTypeCpkLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_VEHICLE_TYPE_CPK_LIST)
      .then((data) => {
        const d = data?.data || data?.result;
        const list = Array.isArray(d) ? d : Array.isArray(data) ? data : [];
        setTypeCpk(list);
        // U-10: remember what came from the API so "Save changed" can diff
        const loaded = {};
        list.forEach((r) => {
          loaded[r.vehicle_type_id] = r.planned_cpk ?? "";
        });
        setLoadedCpk(loaded);
        setTypePage(1);
      })
      .catch((err) => {
        console.log("vehicle-type-cpk list err", err);
        setTypeCpk([]);
        setLoadedCpk({});
      })
      .finally(() => setTypeCpkLoading(false));
  };

  // U-10: a row is "changed" when its planned_cpk differs from the loaded value
  const cpkChanged = (row) => {
    const before = loadedCpk[row.vehicle_type_id];
    const a = isBlank(before) ? "" : String(Number(before));
    const b = isBlank(row.planned_cpk) ? "" : String(Number(row.planned_cpk));
    return a !== b;
  };
  const changedRows = typeCpk.filter(cpkChanged);

  // U-10: search (by type name, case-insensitive) + client-side paging
  const typeName = (r) => String(r.type || r.vehicle_type || r.vehicle_type_id || "");
  const filteredTypeCpk = typeSearch.trim()
    ? typeCpk.filter((r) =>
        typeName(r).toLowerCase().includes(typeSearch.trim().toLowerCase())
      )
    : typeCpk;
  const typeTotal = filteredTypeCpk.length;
  const typePageCount = Math.max(1, Math.ceil(typeTotal / TYPE_PAGE_SIZE));
  const safeTypePage = Math.min(typePage, typePageCount);
  const typeFrom = typeTotal === 0 ? 0 : (safeTypePage - 1) * TYPE_PAGE_SIZE + 1;
  const typeTo = Math.min(safeTypePage * TYPE_PAGE_SIZE, typeTotal);
  const pagedTypeCpk = filteredTypeCpk.slice(
    (safeTypePage - 1) * TYPE_PAGE_SIZE,
    safeTypePage * TYPE_PAGE_SIZE
  );

  const changeTypeCpk = (typeId, value) => {
    setTypeCpk((prev) =>
      prev.map((r) =>
        r.vehicle_type_id === typeId ? { ...r, planned_cpk: value } : r
      )
    );
  };

  // Returns a promise resolving to true on success (used by "Save changed").
  // `quiet` suppresses the per-row toasts during a bulk save.
  const saveTypeCpk = (row, quiet = false) => {
    // FF-11: planned CPK must be >= 0 when given
    if (!isBlank(row.planned_cpk) && !isNonNegativeNumber(row.planned_cpk)) {
      notifyError(`${typeName(row)}: ${t("Planned CPK must be 0 or more")}`);
      return Promise.resolve(false);
    }
    setSavingTypeId(row.vehicle_type_id);
    const body = JSON.stringify({
      vehicle_type_id: row.vehicle_type_id,
      planned_cpk: numOrNull(row.planned_cpk), // FF-18
    });
    return simplePUTCall(ApiConfig.FLEET_FINANCE_VEHICLE_TYPE_CPK_UPDATE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          if (!quiet) notifySuccess(data?.message || "Planned CPK updated");
          // U-10: the saved value becomes the new baseline
          setLoadedCpk((prev) => ({
            ...prev,
            [row.vehicle_type_id]: row.planned_cpk ?? "",
          }));
          return true;
        }
        notifyError(
          `${typeName(row)}: ${data?.message || "Unable to update planned CPK"}`
        );
        return false;
      })
      .catch((err) => {
        console.log("vehicle-type-cpk update err", err);
        notifyError(`${typeName(row)}: ${t("Something went wrong")}`);
        return false;
      })
      .finally(() => setSavingTypeId(null));
  };

  // U-10: save only the rows whose planned_cpk differs from the loaded value,
  // one after another, reusing the single-row update call.
  const saveChangedTypeCpk = async () => {
    const rows = typeCpk.filter(cpkChanged);
    if (rows.length === 0) {
      notifyError(t("Nothing to save"));
      return;
    }
    setSavingAll(true);
    let ok = 0;
    for (const row of rows) {
      // eslint-disable-next-line no-await-in-loop
      if (await saveTypeCpk(row, true)) ok += 1;
    }
    setSavingAll(false);
    if (ok === rows.length) {
      notifySuccess(`${ok} ${t("vehicle type(s) updated")}`);
    } else {
      notifyError(`${ok}/${rows.length} ${t("vehicle type(s) updated")}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSeed = () => {
    setSeeding(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_SEED, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Chart of accounts seeded");
        } else {
          notifyError(data?.message || data?.error || "Unable to seed");
        }
      })
      .catch((err) => {
        console.log("seed err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSeeding(false));
  };

  const handleSave = (e) => {
    e.preventDefault();
    // FF-11: validate before save
    if (!inRange(form.corporate_tax_rate, 0, 100)) {
      notifyError(t("Corporate tax rate must be between 0 and 100"));
      return;
    }
    if (!inRange(form.tax_depreciation_rate, 0, 100)) {
      notifyError(t("Tax depreciation rate must be between 0 and 100"));
      return;
    }
    if (!isPositiveNumber(form.default_expected_km)) {
      notifyError(t("Default expected KM must be greater than 0"));
      return;
    }
    if (
      !isBlank(form.default_planned_cpk) &&
      !isNonNegativeNumber(form.default_planned_cpk)
    ) {
      notifyError(t("Default planned CPK must be 0 or more"));
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      currency_code: form.currency_code,
      corporate_tax_rate: Number(form.corporate_tax_rate),
      tax_depreciation_rate: Number(form.tax_depreciation_rate),
      default_expected_km: Number(form.default_expected_km),
      default_planned_cpk: numOrNull(form.default_planned_cpk), // FF-18
      // INT-13: send auto_post_modules back in the shape the API gave it
      ...(autoPostShape === "array"
        ? { auto_post_modules: AUTO_POST_MODULES.filter((m) => autoPost[m.key]).map((m) => m.key) }
        : autoPostShape === "object"
        ? { auto_post_modules: autoPost }
        : {}),
    });
    simplePUTCall(ApiConfig.FLEET_FINANCE_SETTINGS_UPDATE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Settings updated");
          getSettings();
        } else {
          notifyError(data?.message || data?.error || "Unable to update settings");
        }
      })
      .catch((err) => {
        console.log("update settings err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Settings">
        <div className="main-master-wrapper">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Finance Settings")}
              </p>
            </div>
            {canManage && (
              <button
                className="cx-btn-2"
                onClick={handleSeed}
                disabled={seeding}
              >
                {seeding ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t("Seed Chart of Accounts")
                )}
              </button>
            )}
          </div>

          {loading ? (
            <Loader />
          ) : (
            <Form onSubmit={handleSave}>
              {/* View-only users see the settings but can't change them */}
              <fieldset disabled={!canManage}>
              <div className="innerInputsGen mainVehAccident">
                <div className="addVehicleSe">
                  <div className="addVehicleLeftSec">
                    <div className="row">
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Currency")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("from Customer Profile")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="currency_code"
                            value={
                              currencyName
                                ? `${form.currency_code} ${currencyName}`
                                : form.currency_code
                            }
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Corporate Tax Rate")} (%)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="corporate_tax_rate"
                            value={form.corporate_tax_rate}
                            onChange={handleChange}
                            placeholder="10"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Tax Depreciation Rate")} (%)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="tax_depreciation_rate"
                            value={form.tax_depreciation_rate}
                            onChange={handleChange}
                            placeholder="20"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Default Expected KM")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("annual, utilization fallback")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="default_expected_km"
                            value={form.default_expected_km}
                            onChange={handleChange}
                            placeholder="100000"
                            min="1"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Default Planned CPK")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("CPK variance fallback")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            name="default_planned_cpk"
                            value={form.default_planned_cpk}
                            onChange={handleChange}
                            placeholder="1.0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* INT-13: per-module auto-posting switches (shown only when the API exposes them) */}
              {autoPostShape && (
                <div className="col-md-12 form_input_main" style={{ marginTop: 8 }}>
                  <Form.Label className="common-labels">{t("Auto-post to ledger")}</Form.Label>
                  <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                    {AUTO_POST_MODULES.map((m) => (
                      <Form.Check
                        key={m.key}
                        type="switch"
                        id={`auto-post-${m.key}`}
                        label={t(m.label)}
                        checked={!!autoPost[m.key]}
                        onChange={(e) => setAutoPost((p) => ({ ...p, [m.key]: e.target.checked }))}
                      />
                    ))}
                  </div>
                </div>
              )}
              </fieldset>
              {canManage && (
                <div className="d-flex" style={{ gap: 12, marginTop: 16 }}>
                  <button className="cx-btn-2" type="submit" disabled={saving}>
                    {saving ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t("Save Settings")
                    )}
                  </button>
                </div>
              )}
            </Form>
          )}

          {/* ===== Per-vehicle-type planned CPK ===== */}
          <div
            className="vehicleHead d-flex justify-content-between align-items-center flex-wrap"
            style={{ marginTop: 32, marginBottom: 12, gap: 12 }}
          >
            <p style={{ marginBottom: 0 }}>{t("Vehicle Type Planned CPK")}</p>
            {/* U-10: search + bulk save */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
              <Form.Control
                type="search"
                value={typeSearch}
                onChange={(e) => {
                  setTypeSearch(e.target.value);
                  setTypePage(1);
                }}
                placeholder={t("Search vehicle type")}
                style={{ minWidth: 220 }}
              />
              {canManage && (
              <button
                type="button"
                className="cx-btn-2"
                onClick={saveChangedTypeCpk}
                disabled={savingAll || changedRows.length === 0}
                title={
                  changedRows.length === 0
                    ? t("No unsaved changes")
                    : `${changedRows.length} ${t("row(s) changed")}`
                }
              >
                {savingAll ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  `${t("Save changed")}${
                    changedRows.length ? ` (${changedRows.length})` : ""
                  }`
                )}
              </button>
              )}
            </div>
          </div>
          {typeCpkLoading ? (
            <Loader />
          ) : (
            <div className="VA_table">
              <table className="table tableAdmin">
                <thead className="tableHead">
                  <tr>
                    <th>{t("Vehicle Type")}</th>
                    <th>{t("Vehicles")}</th>
                    <th>{t("Planned CPK")}</th>
                    {canManage && <th>{t("Action")}</th>}
                  </tr>
                </thead>
                <tbody className="tableBody">
                  {pagedTypeCpk.length > 0 ? (
                    pagedTypeCpk.map((r) => (
                      <tr key={r.vehicle_type_id}>
                        <td>{r.type || r.vehicle_type || r.vehicle_type_id}</td>
                        <td>{r.vehicle_count ?? "-"}</td>
                        <td style={{ maxWidth: 160 }}>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={r.planned_cpk ?? ""}
                            disabled={!canManage}
                            onChange={(e) =>
                              changeTypeCpk(r.vehicle_type_id, e.target.value)
                            }
                          />
                        </td>
                        {canManage && (
                        <td>
                          <button
                            type="button"
                            className="cx-btn-1"
                            onClick={() => saveTypeCpk(r)}
                            disabled={savingAll || savingTypeId === r.vehicle_type_id}
                          >
                            {savingTypeId === r.vehicle_type_id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              t("Save")
                            )}
                          </button>
                        </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={canManage ? 4 : 3}>
                        <NoDataComp />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* U-10: client-side paging */}
              {typeTotal > 0 && (
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 12, marginTop: 8 }}
                >
                  <small style={{ opacity: 0.7 }}>
                    {t("Showing")} {typeFrom}–{typeTo} {t("of")} {typeTotal}
                  </small>
                  <div className="d-flex" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="cx-btn-1"
                      onClick={() => setTypePage((p) => Math.max(1, p - 1))}
                      disabled={safeTypePage <= 1}
                    >
                      {t("Previous")}
                    </button>
                    <button
                      type="button"
                      className="cx-btn-1"
                      onClick={() =>
                        setTypePage((p) => Math.min(typePageCount, p + 1))
                      }
                      disabled={safeTypePage >= typePageCount}
                    >
                      {t("Next")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FinanceSettings;
